const SHEET_CSV_URL = "https://docs.google.com/spreadsheets/d/e/2PACX-1vSmgH1bko89oA-PPFoYlmrrPBQ9Ad6e977a46TZNI_fFRf8kyW0XJlCtGXGmrfuyo2RJ1sTGRcgTPhp/pub?gid=0&single=true&output=csv";

/**
 * Parses CSV text into an array of arrays.
 * Basic implementation, handles simple cases.
 * @param {string} csvText The raw CSV text.
 * @returns {string[][]} Array of rows, where each row is an array of cell strings.
 */
const parseCsv = (csvText) => {
    if (!csvText) return [];
    // Split lines, handle potential \r\n or \n
    const lines = csvText.trim().split(/\r?\n/);
    return lines.map(line => {
        // Basic comma split, doesn't handle commas within quotes
        return line.split(',').map(cell => cell.trim());
    });
};

/**
 * Fetches LTP data from a publicly published Google Sheet (as CSV).
 * Assumes the CSV has at least two columns: Symbol (Column 0) and LTP (Column 1).
 * Skips the first row, assuming it's a header.
 * 
 * @returns {Promise<Record<string, string> | null>} A promise that resolves to an object 
 *          mapping symbols to LTPs (e.g., { "SYMBOL1": "123.45", "SYMBOL2": "67.89" }),
 *          or null if fetching/parsing fails or the URL is not configured.
 * @throws {Error} If the environment variable is missing or network errors occur.
 */
export const fetchLtpFromSheet = async () => {
    if (!SHEET_CSV_URL) {
        console.error("Google Sheet CSV URL is missing. Please set REACT_APP_GOOGLE_SHEET_CSV_URL in your .env file.");
        throw new Error("Google Sheet URL configuration error.");
    }

    try {
        const response = await fetch(SHEET_CSV_URL);

        if (!response.ok) {
            const errorBody = await response.text();
            console.error(`Error fetching Google Sheet CSV (${response.status}): ${errorBody}`);
            throw new Error(`Failed to fetch Google Sheet data. Status: ${response.status}`);
        }

        const csvText = await response.text();
        const parsedData = parseCsv(csvText);

        if (parsedData.length < 2) {
            // Need at least a header and one data row (or just one data row if no header)
            console.warn("CSV data seems empty or only contains a header.", parsedData);
            return {}; // Return empty object if no data rows found after header
        }

        const ltpMap = {};
        // Skip header row (index 0)
        for (let i = 1; i < parsedData.length; i++) {
            const row = parsedData[i];
            if (row && row.length >= 2) {
                const symbol = row[0]; // Assuming symbol is in the first column
                const ltp = row[1];    // Assuming LTP is in the second column
                
                // Basic validation: Ensure symbol and LTP look somewhat valid
                if (symbol && ltp && symbol.trim() !== '' && ltp.trim() !== '') {
                    ltpMap[symbol.trim()] = ltp.trim();
                } else {
                    console.warn(`Skipping invalid row ${i + 1} in CSV:`, row);
                }
            } else {
                 console.warn(`Skipping row ${i + 1} due to insufficient columns:`, row);
            }
        }

        return ltpMap;

    } catch (error) {
        console.error("Error fetching or parsing LTP data from Google Sheet:", error);
        throw error; // Re-throw for the caller
    }
}; 